/* Green Gorilla Fruits — referral checkout submit guard
 * --------------------------------------------------------------
 * Intercepts cart drawer + cart page Checkout form submits and routes
 * referral-code holders through the handshake → redeem → autoapply
 * chain before reaching /checkout. Without this guard, a referee can
 * skip /cart entirely (via the drawer Checkout button) and lose the
 * referral coupon because the handshake never fires.
 *
 * Public API: window.GG_ReferralCheckoutGuard.run(event), .clearPending()
 */
(function () {
  'use strict';

  const COUPON_STORAGE_KEY = 'gg_referral_coupon';
  const APPLIED_FLAG_KEY = 'gg_referral_coupon_last_applied';
  // Must stay in sync with assets/customer-account-token.js STORAGE_KEY,
  // referral-handshake.js SESSION_STORAGE_KEY, and the inline bootstrap
  // in layout/theme.liquid. Update all together.
  const SESSION_STORAGE_KEY = 'gg_customer_session';
  const PENDING_CHECKOUT_KEY = 'gg_referral_pending_checkout';
  const FALLBACK_AMOUNT_USD = 10;
  const DEBUG_KEY = 'gg_debug';
  const LOG_PREFIX = '[gg-referral][guard]';

  function debugEnabled() {
    try {
      return localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function log() {
    if (!debugEnabled()) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    try {
      // eslint-disable-next-line no-console
      console.log.apply(console, args);
    } catch (e) {
      /* ignore */
    }
  }

  function getConfig() {
    return window.GG_REFERRAL_CONFIG && typeof window.GG_REFERRAL_CONFIG === 'object'
      ? window.GG_REFERRAL_CONFIG
      : {};
  }

  function getReferralCode() {
    if (window.GG_Referral && typeof window.GG_Referral.getCode === 'function') {
      return window.GG_Referral.getCode();
    }
    return null;
  }

  function readCoupon() {
    try {
      const raw = localStorage.getItem(COUPON_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.code) return null;
      if (parsed.expires_at) {
        const expiry = Date.parse(parsed.expires_at);
        if (!isNaN(expiry) && Date.now() > expiry) return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function couponAlreadyApplied(code) {
    try {
      return localStorage.getItem(APPLIED_FLAG_KEY) === code;
    } catch (e) {
      return false;
    }
  }

  function hasShopifyCustomer() {
    const id = window.GG_SHOPIFY_CUSTOMER_ID;
    if (id === null || id === undefined || id === '') return false;
    return true;
  }

  function hasStoredJwt() {
    try {
      return !!localStorage.getItem(SESSION_STORAGE_KEY);
    } catch (e) {
      // Storage unavailable — assume a JWT exists so we never intercept
      // when we cannot reason about session state.
      return true;
    }
  }

  function setPendingCheckout() {
    try {
      sessionStorage.setItem(PENDING_CHECKOUT_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  }

  function isCartForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    try {
      const url = new URL(form.action, window.location.origin);
      return /\/cart$/.test(url.pathname);
    } catch (e) {
      return false;
    }
  }

  function applyingLabel(coupon) {
    const amount = coupon && coupon.amount_usd != null ? coupon.amount_usd : FALLBACK_AMOUNT_USD;
    return 'Applying $' + amount + ' OFF…';
  }

  function disableButton(btn, label) {
    if (!btn || btn.disabled) return;
    btn.dataset.ggOriginalLabel = btn.textContent;
    btn.textContent = label;
    btn.disabled = true;
  }

  // New Customer Accounts uses `return_to`. If the store migrates back to
  // Classic Customer Accounts the parameter name becomes `checkout_url` —
  // re-test the guest referee flow if that happens.
  function buildLoginUrl(loginUrl, returnTo) {
    const separator = loginUrl.indexOf('?') >= 0 ? '&' : '?';
    return loginUrl + separator + 'return_to=' + encodeURIComponent(returnTo);
  }

  function buildDiscountUrl(code, redirectTo) {
    return '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirectTo);
  }

  function onCheckoutSubmit(event) {
    // Bail when SubmitEvent.submitter is missing (Safari < 15.4 / IE) —
    // the cart-page form has both `name="update"` and `name="checkout"`
    // buttons and we cannot tell which was clicked without the submitter,
    // so the safer default is to skip the intercept and let native submit
    // through. Restoring a DOM-query fallback would hijack quantity updates.
    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
    if (!isCartForm(event.target)) return;

    const code = getReferralCode();
    if (!code) return;

    try {
      const coupon = readCoupon();
      const label = applyingLabel(coupon);

      if (coupon && !couponAlreadyApplied(coupon.code)) {
        event.preventDefault();
        disableButton(submitter, label);
        log('case 2: direct discount→checkout', { code: coupon.code });
        window.location.href = buildDiscountUrl(coupon.code, '/checkout');
        return;
      }

      if (coupon && couponAlreadyApplied(coupon.code)) {
        log('case 6: coupon already applied flag set; letting checkout proceed normally');
        return;
      }

      if (!hasStoredJwt() && hasShopifyCustomer()) {
        event.preventDefault();
        disableButton(submitter, label);
        setPendingCheckout();
        log('case 3: redirecting to /cart to trigger handshake');
        window.location.href = '/cart';
        return;
      }

      if (!hasStoredJwt() && !hasShopifyCustomer()) {
        event.preventDefault();
        disableButton(submitter, label);
        setPendingCheckout();
        const loginUrl = getConfig().accountLoginUrl;
        if (!loginUrl) {
          log('case 4: no accountLoginUrl in config — fallback to /account/login');
        }
        window.location.href = buildLoginUrl(loginUrl || '/account/login', '/cart');
        return;
      }

      if (hasStoredJwt() && !coupon) {
        event.preventDefault();
        disableButton(submitter, label);
        setPendingCheckout();
        log('case 5: JWT present but coupon missing; redirecting to /cart to retry redeem');
        window.location.href = '/cart';
        return;
      }
    } catch (e) {
      // Never trap the user behind a broken handler — log and let the
      // native submit proceed without preventDefault.
      log('onCheckoutSubmit: unexpected error', { error: String(e) });
    }
  }

  document.addEventListener('submit', onCheckoutSubmit, true);

  window.GG_ReferralCheckoutGuard = {
    run: onCheckoutSubmit,
    clearPending: function () {
      try {
        sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
      } catch (e) {
        /* ignore */
      }
    },
  };
})();
